import { z } from 'zod';
// Video preferences schema
export const VideoPreferencesSchema = z.object({
    micEnabled: z.boolean(),
    cameraEnabled: z.boolean(),
    autoJoin: z.boolean()
});
/**
 * Ids are minted server-side as `private_area_<n>` (see PrivateAreaParser).
 * Bounding the shape keeps a client from asking for arbitrary strings that
 * would otherwise reach the LiveKit room namespace.
 */
const AREA_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
/**
 * Video token request schema.
 *
 * There is deliberately no `status` field: availability is a server fact read
 * from `StateManager`, never something the requester asserts about itself. A
 * client that still sends one has it stripped here (zod strips unknown keys),
 * so an old client cannot claim `available` while it is on Do Not Disturb.
 */
export const VideoTokenRequestSchema = z.object({
    areaId: z
        .string()
        .min(1, 'Area ID is required')
        .regex(AREA_ID_PATTERN, 'Invalid area ID'),
    preferences: VideoPreferencesSchema.optional(),
    requestId: z.string().max(128).optional()
});
// Video token response schema
export const VideoTokenResponseSchema = z.object({
    token: z.string().min(1, 'Token is required'),
    roomName: z.string().min(1, 'Room name is required'),
    url: z.string().url('Invalid URL'),
    requestId: z.string().optional()
});
// Video error schema
export const VideoErrorSchema = z.object({
    code: z.string().min(1, 'Error code is required'),
    message: z.string().optional(),
    requestId: z.string().optional()
});
// Video area event schemas
export const VideoAreaEnteredSchema = z.object({
    areaId: z.string().min(1, 'Area ID is required'),
    roomId: z.string().min(1, 'Room ID is required'),
    timestamp: z.number()
});
export const VideoAreaLeftSchema = z.object({
    areaId: z.string().min(1, 'Area ID is required'),
    roomId: z.string().min(1, 'Room ID is required'),
    timestamp: z.number()
});
/**
 * Private area update data for video handler.
 *
 * `occupants` is optional, and an absent list means "this update does not know
 * who is left in the area" - never "the area is empty". Consumers must not end
 * a call on the strength of a missing list; see `VideoHandler`.
 */
export const PrivateAreaUpdateDataSchema = z.object({
    areaId: z.string().min(1, 'Area ID is required'),
    action: z.enum(['player_entered', 'player_left', 'closed', 'opened']),
    area: z.object({
        occupants: z.array(z.any()).optional()
    }).optional(),
    userId: z.string().optional(),
    roomId: z.string().optional()
});
